import React, { useState, useEffect, useRef } from 'react';
import { playSpookySound } from './spookyAudio';

const MOON_MESSAGES = [
    '🩸 THE BLOOD MOON RISES! Dark spirits awaken...',
    '🌕 The witching hour is upon us! Beware the shadows...',
    '🦇 A swarm of nocturnal creatures circles the midnight sky...',
    '🔮 The lunar veil thins... ancient magic fills the store!'
];

const BloodMoon = ({ onTriggerScare }) => {
    const [isBloodMoon, setIsBloodMoon] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [moonMessage, setMoonMessage] = useState('');
    const [flyingWitch, setFlyingWitch] = useState(false);
    const messageTimeoutRef = useRef(null);

    // Periodically trigger a flying witch across the moon (every 25-35s)
    useEffect(() => {
        const scheduleWitch = () => {
            const delay = 22000 + Math.random() * 15000;
            return setTimeout(() => {
                setFlyingWitch(true);
                setTimeout(() => setFlyingWitch(false), 6000);
                witchTimer = scheduleWitch();
            }, delay);
        };
        let witchTimer = scheduleWitch();
        return () => clearTimeout(witchTimer);
    }, []);

    const handleMoonClick = () => {
        setIsBloodMoon(prev => !prev);
        setFlyingWitch(true);
        setTimeout(() => setFlyingWitch(false), 5500);

        playSpookySound('howl');
        setTimeout(() => playSpookySound('thunder'), 300);
        setTimeout(() => playSpookySound('cackle'), 700);

        const randomMsg = MOON_MESSAGES[Math.floor(Math.random() * MOON_MESSAGES.length)];
        setMoonMessage(randomMsg);
        if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = setTimeout(() => setMoonMessage(''), 3000);

        if (onTriggerScare) {
            onTriggerScare();
        }
    };

    return (
        <>
            {/* Blood Moon Announcement Popup - Centered & Screen Safe */}
            {moonMessage && (
                <div className="fixed top-14 md:top-20 inset-x-0 flex justify-center z-[76] pointer-events-none px-4">
                    <div className="w-full max-w-sm sm:max-w-md animate-bounce">
                        <div className="bg-gradient-to-r from-red-950 via-purple-950 to-orange-950 text-orange-200 text-xs sm:text-sm font-bold px-4 py-2.5 rounded-2xl shadow-2xl shadow-red-950/80 border border-orange-500/60 backdrop-blur-md text-center">
                            {moonMessage}
                        </div>
                    </div>
                </div>
            )}

            {/* The Harvest / Blood Moon in Sky (Desktop-only to prevent mobile header & category collisions) */}
            <div className="hidden md:block fixed top-20 right-8 lg:right-16 z-[1] pointer-events-none select-none">
                {/* Speech Tooltip on Hover */}
                {hovered && !moonMessage && (
                    <div className="absolute top-full mt-2 -left-12 sm:-left-8 bg-gray-950/95 text-orange-300 border border-orange-500/50 text-[11px] font-semibold px-2.5 py-1 rounded-xl shadow-xl shadow-black/70 whitespace-nowrap animate-bounce pointer-events-none z-10">
                        {isBloodMoon ? 'Click to restore Harvest Moon 🌕' : 'Click to summon Blood Moon 🩸'}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-950 border-t border-l border-orange-500/50 rotate-45" />
                    </div>
                )}

                <div
                    onClick={handleMoonClick}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="relative cursor-pointer pointer-events-auto transition-transform duration-500 hover:scale-110 active:scale-95 group"
                    title={isBloodMoon ? 'Click to restore Harvest Moon' : 'Click to summon the Blood Moon!'}
                >
                    {/* Atmospheric Moon Glow Aura */}
                    <div
                        className={`absolute -inset-4 rounded-full blur-2xl transition-all duration-700 pointer-events-none ${
                            isBloodMoon
                                ? 'bg-red-600/50 group-hover:bg-red-600/70 animate-pulse'
                                : 'bg-amber-500/35 group-hover:bg-amber-500/55'
                        }`}
                    />

                    {/* Moon Disc SVG */}
                    <svg
                        width="84"
                        height="84"
                        viewBox="0 0 100 100"
                        className={`relative transition-all duration-700 ${
                            isBloodMoon
                                ? 'drop-shadow-[0_0_25px_rgba(239,68,68,0.95)]'
                                : 'drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]'
                        }`}
                    >
                        <defs>
                            <radialGradient id="harvestMoonGrad" cx="38%" cy="38%" r="62%">
                                <stop offset="0%" stopColor="#fef08a" />
                                <stop offset="35%" stopColor="#fde047" />
                                <stop offset="75%" stopColor="#f59e0b" />
                                <stop offset="100%" stopColor="#d97706" />
                            </radialGradient>
                            <radialGradient id="bloodMoonGrad" cx="38%" cy="38%" r="62%">
                                <stop offset="0%" stopColor="#fca5a5" />
                                <stop offset="40%" stopColor="#ef4444" />
                                <stop offset="75%" stopColor="#b91c1c" />
                                <stop offset="100%" stopColor="#450a0a" />
                            </radialGradient>
                            <radialGradient id="craterGrad" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#000000" stopOpacity="0.25" />
                                <stop offset="100%" stopColor="#000000" stopOpacity="0.05" />
                            </radialGradient>
                        </defs>

                        {/* Outer Disc */}
                        <circle
                            cx="50"
                            cy="50"
                            r="44"
                            fill={isBloodMoon ? 'url(#bloodMoonGrad)' : 'url(#harvestMoonGrad)'}
                            stroke={isBloodMoon ? '#7f1d1d' : '#b45309'}
                            strokeWidth="1.2"
                        />

                        {/* Realistic Crater Shading */}
                        <circle cx="36" cy="34" r="8" fill="url(#craterGrad)" />
                        <circle cx="62" cy="42" r="11" fill="url(#craterGrad)" />
                        <circle cx="45" cy="65" r="9.5" fill="url(#craterGrad)" />
                        <circle cx="28" cy="56" r="6" fill="url(#craterGrad)" />
                        <circle cx="70" cy="66" r="5" fill="url(#craterGrad)" />
                        <circle cx="52" cy="22" r="4.5" fill="url(#craterGrad)" />

                        {/* Silhouetted Gnarled Tree Branch across top-left */}
                        <path
                            d="M 6 12 Q 22 25 38 22 Q 46 20 54 26 M 22 25 Q 26 36 32 38 M 38 22 Q 44 14 50 16"
                            stroke="#09090b"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            fill="none"
                            opacity="0.85"
                        />

                        {/* Subtle Silhouetted Bats around Moon */}
                        <path
                            d="M 68 28 Q 72 24 76 28 Q 74 31 72 30 Q 70 31 68 28 Z"
                            fill="#09090b"
                            opacity="0.8"
                        />
                        <path
                            d="M 76 34 Q 79 31 82 34 Q 80 36 79 35 Q 78 36 76 34 Z"
                            fill="#09090b"
                            opacity="0.75"
                        />

                        {/* Eerie Mist Swirl Across Face */}
                        <path
                            d="M 12 58 Q 38 48 64 56 Q 80 62 92 52"
                            stroke="rgba(255, 255, 255, 0.25)"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            fill="none"
                            className="animate-pulse"
                        />
                    </svg>

                    {/* Silhouetted Flying Witch on Broom (Animates across screen) */}
                    {flyingWitch && (
                        <div className="absolute -top-3 -right-6 animate-witch-flight pointer-events-none select-none">
                            <svg width="48" height="48" viewBox="0 0 100 100" className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                                {/* Witch Silhouette */}
                                <polygon points="45,15 35,42 65,42" fill="#09090b" />
                                <ellipse cx="50" cy="42" rx="18" ry="3.5" fill="#09090b" />
                                <circle cx="48" cy="46" r="6" fill="#09090b" />
                                <polygon points="42,46 36,49 42,48" fill="#09090b" />
                                <path d="M 44 50 L 30 75 L 62 72 L 52 50 Z" fill="#09090b" />
                                <line x1="12" y1="78" x2="88" y2="52" stroke="#78350f" strokeWidth="3" strokeLinecap="round" />
                                <path d="M 12 78 L 2 84 L 5 74 L 14 76 Z" fill="#d97706" />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BloodMoon;
